'use client';

import { c as _c } from "react/compiler-runtime";
import { GoogleOAuthProvider } from '@react-oauth/google';
import { ALL_PROVIDERS, type OAuthProviderName, providerLabel } from '@shared/all/domains/authMethods';
import { MIN_PASSWORD_LENGTH } from '@shared/all/validators';
import toast from '@shared/utils/helpers/toast';
import usePasskey from '@shared/utils/hooks/usePasskey';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { KeyRound, Lock, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { client } from '../../utils/trpc';
import OAuthButtons from '../auth/OAuthButtons';
import Button from '../common/Button';
import PasswordInput from '../UI/PasswordInput';
const emptyPw = {
  open: false,
  current: '',
  value: '',
  confirm: ''
};

// Every sign-in method in one place: passkeys, password (add or change), and
// linked Google/Apple. The server enforces "keep at least one method"; this
// mirrors it so the last remaining method can't even be clicked.
const SignInMethods = () => {
  const $ = _c(70);
  const queryClient = useQueryClient();
  const {
    addPasskey,
    busy: passkeyBusy
  } = usePasskey(client);
  const [pw, setPw] = useState(emptyPw);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      queryKey: ["authMethods", "mine"],
      queryFn: _temp
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const methodsQuery = useQuery(t0);
  let t1;
  if ($[1] !== queryClient) {
    t1 = () => queryClient.invalidateQueries({
      queryKey: ["authMethods", "mine"]
    });
    $[1] = queryClient;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const refresh = t1;
  const data_0 = methodsQuery.data;
  if (!data_0) {
    return null;
  }
  const methodCount = (data_0.hasPassword ? 1 : 0) + data_0.passkeys.length + data_0.oauthProviders.length;
  const isLastMethod = methodCount <= 1;
  const guardLast = _temp2;
  let t2;
  if ($[3] !== addPasskey || $[4] !== refresh) {
    t2 = async () => {
      const ok = await addPasskey();
      if (ok) {
        toast("Passkey added.");
        refresh();
      }
    };
    $[3] = addPasskey;
    $[4] = refresh;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const handleAddPasskey = t2;
  let t3;
  if ($[6] !== isLastMethod || $[7] !== refresh) {
    t3 = async id => {
      if (isLastMethod) {
        return guardLast();
      }
      const [error_0] = await client.auth.passkey.remove.mutate({
        id
      });
      if (!error_0) {
        toast("Passkey removed.");
        refresh();
      }
    };
    $[6] = isLastMethod;
    $[7] = refresh;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const handleRemovePasskey = t3;
  let t4;
  if ($[9] !== data_0.hasPassword || $[10] !== pw.confirm || $[11] !== pw.current || $[12] !== pw.value || $[13] !== refresh) {
    t4 = async () => {
      if (pw.value.length < MIN_PASSWORD_LENGTH) {
        return toast(`Password must be at least ${MIN_PASSWORD_LENGTH} characters.`, "", "error");
      }
      if (pw.value !== pw.confirm) {
        return toast("Passwords do not match.", "", "error");
      }
      if (data_0.hasPassword) {
        const [error_1] = await client.auth.changePassword.mutate({
          currentPassword: pw.current,
          newPassword: pw.value
        });
        if (error_1) {
          return;
        }
        toast("Password changed.");
      } else {
        const [error_2] = await client.auth.setPassword.mutate({
          password: pw.value
        });
        if (error_2) {
          return;
        }
        toast("Password added.");
      }
      setPw(emptyPw);
      refresh();
    };
    $[9] = data_0.hasPassword;
    $[10] = pw.confirm;
    $[11] = pw.current;
    $[12] = pw.value;
    $[13] = refresh;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  const handleSavePassword = t4;
  let t5;
  if ($[15] !== refresh) {
    t5 = async (provider, idToken, user) => {
      const [error_3] = await client.auth.oAuthLink.mutate({
        provider,
        idToken,
        user
      });
      if (!error_3) {
        toast(`${providerLabel(provider)} linked.`);
        refresh();
      }
    };
    $[15] = refresh;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  const handleLink = t5;
  let t6;
  if ($[17] !== isLastMethod || $[18] !== refresh) {
    t6 = async provider_0 => {
      if (isLastMethod) {
        return guardLast();
      }
      const [error_4] = await client.auth.oAuthUnlink.mutate({
        provider: provider_0
      });
      if (!error_4) {
        toast(`${providerLabel(provider_0)} unlinked.`);
        refresh();
      }
    };
    $[17] = isLastMethod;
    $[18] = refresh;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  const handleUnlink = t6;
  let t7;
  if ($[20] !== data_0.oauthProviders) {
    t7 = ALL_PROVIDERS.filter(p => !data_0.oauthProviders.includes(p));
    $[20] = data_0.oauthProviders;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  const unlinkedProviders = t7;
  const saveDisabled = pw.value.length < MIN_PASSWORD_LENGTH || pw.value !== pw.confirm || data_0.hasPassword && pw.current.length < MIN_PASSWORD_LENGTH;
  let t8;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <h3 className="font-emphasis">Passkeys</h3>;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  let t9;
  if ($[23] !== data_0.passkeys.length) {
    t9 = data_0.passkeys.length === 0 && <p className="text-sm text-textSecondary">None yet. A passkey signs you in with your face, fingerprint or device PIN.</p>;
    $[23] = data_0.passkeys.length;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[25] !== data_0.passkeys || $[26] !== handleRemovePasskey || $[27] !== isLastMethod) {
    let t11;
    if ($[29] !== handleRemovePasskey || $[30] !== isLastMethod) {
      t11 = p_0 => <div key={p_0.id} className="flex items-center justify-between rounded-lg bg-background-secondary p-3"><div className="flex items-center gap-2"><KeyRound size={18} /><div><p className="text-sm font-emphasis">{p_0.name || "Passkey"}</p><p className="text-xs text-textSecondary">Added {new Date(p_0.createdAt).toLocaleDateString()}</p></div></div><button type="button" className="flex items-center gap-1 text-sm text-destructive hover:underline disabled:opacity-40" disabled={isLastMethod} onClick={() => handleRemovePasskey(p_0.id)}><Trash2 size={16} /> Remove</button></div>;
      $[29] = handleRemovePasskey;
      $[30] = isLastMethod;
      $[31] = t11;
    } else {
      t11 = $[31];
    }
    t10 = data_0.passkeys.map(t11);
    $[25] = data_0.passkeys;
    $[26] = handleRemovePasskey;
    $[27] = isLastMethod;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  let t11;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <KeyRound size={18} />;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  let t12;
  if ($[33] !== handleAddPasskey || $[34] !== passkeyBusy) {
    t12 = <Button variant="SECONDARY" text="Add a passkey" icon={t11} classNames="w-full" loading={passkeyBusy} onClick={handleAddPasskey} />;
    $[33] = handleAddPasskey;
    $[34] = passkeyBusy;
    $[35] = t12;
  } else {
    t12 = $[35];
  }
  let t13;
  if ($[36] !== t10 || $[37] !== t12 || $[38] !== t9) {
    t13 = <section className="space-y-3">{t8}{t9}{t10}{t12}</section>;
    $[36] = t10;
    $[37] = t12;
    $[38] = t9;
    $[39] = t13;
  } else {
    t13 = $[39];
  }
  let t14;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <h3 className="font-emphasis">Password</h3>;
    $[40] = t14;
  } else {
    t14 = $[40];
  }
  let t15;
  if ($[41] !== data_0.hasPassword || $[42] !== pw.open) {
    t15 = !pw.open && <div className="flex items-center justify-between rounded-lg bg-background-secondary p-3"><div className="flex items-center gap-2"><Lock size={18} /><p className="text-sm font-emphasis">{data_0.hasPassword ? "Password set" : "No password"}</p></div><button type="button" className="text-sm text-primary hover:underline" onClick={() => setPw({
        ...emptyPw,
        open: true
      })}>{data_0.hasPassword ? "Change" : "Add"}</button></div>;
    $[41] = data_0.hasPassword;
    $[42] = pw.open;
    $[43] = t15;
  } else {
    t15 = $[43];
  }
  let t16;
  if ($[44] !== data_0.hasPassword || $[45] !== handleSavePassword || $[46] !== pw || $[47] !== saveDisabled) {
    t16 = pw.open && <div className="space-y-2 rounded-lg bg-background-secondary p-3">{data_0.hasPassword && <PasswordInput label="Current password" value={pw.current} onChange={current => setPw({
        ...pw,
        current
      })} />}<PasswordInput label="New password" value={pw.value} onChange={value => setPw({
        ...pw,
        value
      })} /><PasswordInput label="Confirm password" value={pw.confirm} onChange={confirm => setPw({
        ...pw,
        confirm
      })} />{data_0.hasPassword && <p className="text-xs text-textSecondary">Changing your password signs you out on your other devices.</p>}<div className="flex gap-2"><Button text="Save" classNames="flex-1" disabled={saveDisabled} onClick={handleSavePassword} /><Button variant="SECONDARY" text="Cancel" onClick={() => setPw(emptyPw)} /></div></div>;
    $[44] = data_0.hasPassword;
    $[45] = handleSavePassword;
    $[46] = pw;
    $[47] = saveDisabled;
    $[48] = t16;
  } else {
    t16 = $[48];
  }
  let t17;
  if ($[49] !== t15 || $[50] !== t16) {
    t17 = <section className="space-y-3">{t14}{t15}{t16}</section>;
    $[49] = t15;
    $[50] = t16;
    $[51] = t17;
  } else {
    t17 = $[51];
  }
  let t18;
  if ($[52] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <h3 className="font-emphasis">{"Google & Apple"}</h3>;
    $[52] = t18;
  } else {
    t18 = $[52];
  }
  let t19;
  if ($[53] !== data_0.oauthProviders || $[54] !== handleUnlink || $[55] !== isLastMethod) {
    let t20;
    if ($[57] !== handleUnlink || $[58] !== isLastMethod) {
      t20 = provider_1 => <div key={provider_1} className="flex items-center justify-between rounded-lg bg-background-secondary p-3"><p className="text-sm font-emphasis">{providerLabel(provider_1)} connected</p><button type="button" className="text-sm text-destructive hover:underline disabled:opacity-40" disabled={isLastMethod} onClick={() => handleUnlink(provider_1)}>Unlink</button></div>;
      $[57] = handleUnlink;
      $[58] = isLastMethod;
      $[59] = t20;
    } else {
      t20 = $[59];
    }
    t19 = data_0.oauthProviders.map(t20);
    $[53] = data_0.oauthProviders;
    $[54] = handleUnlink;
    $[55] = isLastMethod;
    $[56] = t19;
  } else {
    t19 = $[56];
  }
  let t20;
  if ($[60] !== handleLink || $[61] !== unlinkedProviders) {
    t20 = unlinkedProviders.length > 0 && <GoogleOAuthProvider clientId={process.env.NEXT_PUBLIC_GOOGLE_WEB_CLIENT_ID}><OAuthButtons providers={unlinkedProviders} onToken={handleLink} showDivider={false} /></GoogleOAuthProvider>;
    $[60] = handleLink;
    $[61] = unlinkedProviders;
    $[62] = t20;
  } else {
    t20 = $[62];
  }
  let t21;
  if ($[63] !== t19 || $[64] !== t20) {
    t21 = <section className="space-y-3">{t18}{t19}{t20}</section>;
    $[63] = t19;
    $[64] = t20;
    $[65] = t21;
  } else {
    t21 = $[65];
  }
  let t22;
  if ($[66] !== t13 || $[67] !== t17 || $[68] !== t21) {
    t22 = <div className="flex flex-col space-y-8">{t13}{t17}{t21}</div>;
    $[66] = t13;
    $[67] = t17;
    $[68] = t21;
    $[69] = t22;
  } else {
    t22 = $[69];
  }
  return t22;
};
export default SignInMethods;
async function _temp() {
  const [error, data] = await client.authMethods.mine.query();
  if (error) {
    throw error;
  }
  return data;
}
function _temp2() {
  return toast("Keep at least one way to sign in.", "", "error");
}